import { api, ApiError, type RolesResponse } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";

export type IdentityMode = "pending" | "v2" | "legacy" | "updating" | "stopped" | "error";

export interface IdentityState {
  mode: IdentityMode;
  error: unknown;
  retry: () => void;
}

export const rolesKey = (containerId: string) => ["boo-roles", containerId];
export const peopleKey = (containerId: string) => ["boo-people", containerId];

function isLegacy(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.code === "brain_outdated";
}

function isUpdating(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503 && error.code === "brain_upgrading";
}

export function useRoles(containerId: string | undefined, enabled = true) {
  return useQuery<RolesResponse>({
    queryKey: rolesKey(containerId ?? ""),
    queryFn: () => api.containers.roles.get(containerId ?? ""),
    enabled: !!containerId && enabled,
    retry: (count, error) => !isLegacy(error) && !isUpdating(error) && count < 2,
    refetchInterval: (query) => (isUpdating(query.state.error) ? 15_000 : false),
  });
}

export function useIdentityMode(containerId: string | undefined): IdentityState {
  const boos = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const boo = boos.data?.data.find((b) => b.id === containerId);
  const running = boo?.deploymentStatus === "running" && boo._permissions?.canView !== false;
  const roles = useRoles(containerId, running);
  if (!boos.data) {
    return { mode: boos.isError ? "error" : "pending", error: boos.error, retry: () => void boos.refetch() };
  }
  const state = (mode: IdentityMode): IdentityState => ({ mode, error: roles.error, retry: () => void roles.refetch() });
  if (roles.data) return state("v2");
  if (!running) return state("stopped");
  if (roles.isError) {
    if (isLegacy(roles.error)) return state("legacy");
    if (isUpdating(roles.error)) return state("updating");
    return state("error");
  }
  return state("pending");
}
